/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-32 (22/10) — LUẬT của khung "Vấn đề" (`components/HopVanDe.tsx`), tách ra THUẦN: không React, không API —
 * `kiem-tk32` nhập thẳng và CHẠY (tổng các tab = số việc đang mở; mỗi hồ sơ đúng một tab mức; giả lập không lên
 * phạm vi hệ thống). Khung chỉ vẽ.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import { laMatKetNoi, phanMuc } from './canhbao';
import { tenViec } from './loi_nguoi';
import type { Alarm } from './types';

export type TabVanDe = 'mo' | 'khan' | 'canh' | 'mat' | 'chuadoc' | 'xong';
export type NguonVanDe = 'tat' | 'tu' | 'nema';
export interface DongChuaDoc { id: string; tieu: string; noi?: string; onDoc?: () => void }
export interface VanDeGiaLapVao { id: string; addr: number | null; code: string; mucDo: number; message: string; raisedAt: string }

export type Muc = 'khan' | 'canh' | 'mat';
export interface Dong {
  id: string; muc: Muc | 'chuadoc' | 'xong'; nguon: NguonVanDe; tieu: string; noi: string; luc: string | null;
  alarm?: Alarm; giaLap?: boolean; daNhan?: boolean; onDoc?: () => void; cabinetId?: string;
}

/** Tên việc NGẮN cho dòng vấn đề đèn NEMA (quy tắc 22/10: nhãn ngắn, không diễn giải). Mã lạ ⇒ tên dài của bảng dịch. */
export const VIEC_NGAN: Record<string, string> = {
  NEMA_LAMP_LOST: 'mất kết nối', NEMA_LAMP_DARK: 'không sáng', NEMA_LAMP_NO_POWER: 'mất điện', NEMA_METER_FAIL: 'lỗi đo',
  NEMA_METER_NOISE: 'đo nhiễu', NEMA_GPS_NOFIX: 'chưa có vị trí', NEMA_GPS_DEAD: 'mất GPS', NEMA_POWER_DEV: 'công suất lệch',
  NEMA_HOST_NO_RIIM: 'card lỗi RF', NEMA_HOST_LOST: 'card mất kết nối', NEMA_NET_SLOW: 'mạng lên chậm', NEMA_RF_CFG: 'cấu hình RF sai',
  NEMA_NHANH_MAT_DIEN: 'nhánh không có điện',
};

/** Mức của MỘT hồ sơ: mức riêng của hàng qua `phanMuc` (không bộ phân mức thứ hai), Mất kết nối qua `laMatKetNoi`. */
export function mucCua(a: Pick<Alarm, 'code' | 'mucDo'>): Muc {
  const pm = phanMuc(a.code, a.mucDo);
  if (laMatKetNoi(pm.ma || a.code)) return 'mat';
  return pm.mucDo === 1 ? 'khan' : 'canh';
}

/**
 * Dựng mọi dòng của khung. `heThong` ⇒ vấn đề GIẢ LẬP bị BỎ (chốt TK-31 mốc 3: không bao giờ lên phạm vi hệ thống);
 * `cabinetId` ⇒ "Đã xử lý" chỉ của tủ ấy.
 */
export function dungDong(v: {
  mo: Alarm[] | undefined; xong: Alarm[] | undefined; vanDeGiaLap?: VanDeGiaLapVao[]; chuaDoc?: DongChuaDoc[];
  heThong: boolean; cabinetId?: string; tenDen?: (addr: number) => string;
}): Dong[] {
  const r: Dong[] = [];
  /* Tên đèn: tab Đèn truyền `tenDen` (cùng `lib/nemaNhan.ts`); phạm vi hệ thống đọc `nemaTen` máy chủ dựng (cùng `sttTheoAddr`). */
  const ten = (addr: number | null | undefined, tu: string, nemaTen?: string | null) =>
    addr == null ? tu : (v.tenDen ? v.tenDen(addr) : nemaTen ?? 'Đèn');
  for (const a of v.mo ?? []) {
    const nema = a.nemaAddr != null || a.code.startsWith('NEMA_');
    const tu = a.cabinet?.name ?? 'Tủ';
    r.push({
      id: a.id, muc: mucCua(a), nguon: nema ? 'nema' : 'tu', alarm: a, cabinetId: a.cabinetId,
      tieu: nema ? `${a.nemaAddr != null ? ten(a.nemaAddr, tu, a.nemaTen) : tu} ${VIEC_NGAN[a.code] ?? tenViec(a.code)}` : `${tu} — ${tenViec(a.code)}`,
      noi: nema ? tu : (a.cabinet?.code ?? ''), luc: a.raisedAt, daNhan: !!a.ackedAt,
    });
  }
  if (!v.heThong) {
    for (const g of v.vanDeGiaLap ?? []) {
      const muc: Muc = laMatKetNoi(g.code) ? 'mat' : g.mucDo === 1 ? 'khan' : 'canh';
      r.push({ id: `gl-${g.id}`, muc, nguon: 'nema', giaLap: true, tieu: `${g.addr != null ? ten(g.addr, 'Card') : 'Card'} ${VIEC_NGAN[g.code] ?? tenViec(g.code)}`, noi: 'giả lập', luc: g.raisedAt });
    }
  }
  for (const c of v.chuaDoc ?? []) r.push({ id: `cd-${c.id}`, muc: 'chuadoc', nguon: 'tu', tieu: c.tieu, noi: c.noi ?? '', luc: null, onDoc: c.onDoc });
  for (const a of v.xong ?? []) {
    if (v.cabinetId && a.cabinetId !== v.cabinetId) continue;
    const nema = a.nemaAddr != null || a.code.startsWith('NEMA_');
    r.push({ id: a.id, muc: 'xong', nguon: nema ? 'nema' : 'tu', alarm: a, cabinetId: a.cabinetId, tieu: `${a.cabinet?.name ?? 'Tủ'} — ${tenViec(a.code)}`, noi: 'đã xử lý', luc: a.clearedAt, daNhan: true });
  }
  return r;
}

/** Bộ lọc của TỪNG tab. "Đang mở" = Khẩn ∪ Cảnh báo ∪ Mất kết nối — ba tab mức chia nó không chồng, không sót. */
export const LOC_TAB: Record<TabVanDe, (d: Dong) => boolean> = {
  mo: (d) => d.muc === 'khan' || d.muc === 'canh' || d.muc === 'mat',
  khan: (d) => d.muc === 'khan',
  canh: (d) => d.muc === 'canh',
  mat: (d) => d.muc === 'mat',
  chuadoc: (d) => d.muc === 'chuadoc',
  xong: (d) => d.muc === 'xong',
};

/**
 * TK-33b (lee 22/10 chiều) — gấp số đếm thô của `GET /api/alarms/dem` (theo mã + mức riêng) thành 4 số của trang chính,
 *   bằng CÙNG `mucCua` của khung ⇒ ô "Sự cố đang mở", chuông, tab và chip KHÔNG phụ thuộc trần 100 hàng mà vẫn một luật mức.
 */
export interface DemSuCo { mo: number; khan: number; canh: number; mat: number }
export function demTheoNhom(theoMa: { code: string; mucDo: number | null; n: number }[]): DemSuCo {
  const d: DemSuCo = { mo: 0, khan: 0, canh: 0, mat: 0 };
  for (const h of theoMa) {
    /* Mức riêng chỉ nhận 1 · 2 (như `phanMuc`): số lạ từ CSDL rơi về bảng theo mã, không đi thẳng vào phân nhóm. */
    d[mucCua({ code: h.code, mucDo: h.mucDo === 1 || h.mucDo === 2 ? h.mucDo : null })] += h.n;
    d.mo += h.n;
  }
  return d;
}

/**
 * Số "sự cố đang mở" của MỌI chỗ hiện nó (ô số trang chính · chuông · tab Trung tâm sự cố): số máy chủ đếm (không trần);
 *   số đếm chưa về ⇒ đếm trên dòng đã tải. MỘT hàm, ba chỗ gọi ⇒ ba chỗ không thể lệch nhau.
 */
export function soSuCoMo(dem: DemSuCo | null, dong: Dong[]): number {
  return dem?.mo ?? dong.filter(LOC_TAB.mo).length;
}
